export type ParsedPfmNodeListNode = {
  index: number;
  key: string;
  title: string;
};

export type ParsedPfmNodeList = {
  title: string;
  description: string;
  nodes: ParsedPfmNodeListNode[];
};

function normalizeNodeKey(title: string): string {
  return title
    .trim()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .replace(/[^A-Za-z0-9/-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function cleanNodeTitle(value: string): string {
  return value
    .trim()
    .replace(/^\|+|\|+$/g, "")
    .replace(/^`|`$/g, "")
    .replace(/\*\*/g, "")
    .replace(/^["']|["']$/g, "")
    .replace(/[.,;:]+$/g, "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function splitNodeKeyAndTitle(value: string): { key: string; title: string } {
  const cleaned = cleanNodeTitle(value);
  const markdownCells = cleaned
    .split("|")
    .map((cell) => cleanNodeTitle(cell))
    .filter(Boolean);
  if (markdownCells.length >= 2) {
    return {
      key: normalizeNodeKey(markdownCells[0]),
      title: markdownCells.slice(1).join(" "),
    };
  }

  const dashMatch = cleaned.match(/^([A-Za-z][A-Za-z0-9]*(?:[-_/][A-Za-z0-9]+)+)\s+[-–—]\s+(.+)$/);
  if (dashMatch) {
    return {
      key: normalizeNodeKey(dashMatch[1]),
      title: cleanNodeTitle(dashMatch[2]),
    };
  }

  // Tolerate agent replies like:
  // 1. **Introduction** — Overview text
  // 2. Employee Login - Description
  // If the left side is not already a key, derive a stable key from it.
  const genericDashMatch = cleaned.match(/^(.+?)\s+[-–—]\s+(.+)$/);
  if (genericDashMatch) {
    const left = cleanNodeTitle(genericDashMatch[1]);
    const right = cleanNodeTitle(genericDashMatch[2]);
    const maybeKey = left.match(/^[A-Za-z][A-Za-z0-9]*(?:[-_/][A-Za-z0-9]+)+$/)
      ? normalizeNodeKey(left)
      : normalizeNodeKey(left.toLowerCase().replace(/\s+/g, "-"));
    return {
      key: maybeKey,
      title: left || right,
    };
  }

  const keyTitleMatch = cleaned.match(/^([A-Za-z][A-Za-z0-9]*(?:[-_/][A-Za-z0-9]+)+)\s+(.+)$/);
  if (keyTitleMatch) {
    return {
      key: normalizeNodeKey(keyTitleMatch[1]),
      title: cleanNodeTitle(keyTitleMatch[2]),
    };
  }

  const derivedKey = normalizeNodeKey(cleaned.toLowerCase().replace(/\s+/g, "-"));
  return {
    key: derivedKey,
    title: cleaned,
  };
}

/**
 * Combined node-report-plus-children replies use a `[CHILD-PFM-NODES]` marker
 * to separate the Markdown report (above) from the numbered child PFM node
 * list (below). This helper splits the two halves so each half can be handled
 * by its own consumer (renderer for the report, parser for the child list).
 *
 * The split is permissive: the marker is matched at the start of any line,
 * with optional brackets/whitespace, case-insensitive. When the marker is
 * absent, the entire reply is returned as the report and the children section
 * is empty so the report still renders gracefully.
 */
const CHILD_PFM_DELIMITER_RE = /^\s*\[?\s*CHILD[-_ ]PFM[-_ ]NODES\s*\]?\s*:?\s*$/im;

export function splitNodeReportAndChildren(replyText: string): {
  reportMarkdown: string;
  childListText: string;
} {
  const raw = String(replyText || "");
  if (!raw.trim()) {
    return { reportMarkdown: "", childListText: "" };
  }
  const match = raw.match(CHILD_PFM_DELIMITER_RE);
  if (!match || typeof match.index !== "number") {
    return { reportMarkdown: raw.trim(), childListText: "" };
  }
  const before = raw.slice(0, match.index).trim();
  const after = raw.slice(match.index + match[0].length).trim();
  const noneOnly = after.replace(/[\s`*_-]+/g, " ").trim().toLowerCase();
  if (noneOnly === "(none)" || noneOnly === "none") {
    return { reportMarkdown: before, childListText: "" };
  }
  return { reportMarkdown: before, childListText: after };
}

export function parsePfmNodeListResponse(responseText: string): ParsedPfmNodeList {
  const lines = String(responseText || "").split(/\r?\n/);
  let title = "";
  let description = "";
  const nodes: ParsedPfmNodeListNode[] = [];
  let activeIndex: number | null = null;
  let activeText = "";

  const flushNode = () => {
    if (activeIndex == null) return;
    const { key, title: nodeTitle } = splitNodeKeyAndTitle(activeText);
    if (!nodeTitle) {
      activeIndex = null;
      activeText = "";
      return;
    }
    if (key && !nodes.some((n) => n.index === activeIndex || n.key === key)) {
      nodes.push({ index: activeIndex, key, title: nodeTitle });
    }
    activeIndex = null;
    activeText = "";
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      flushNode();
      continue;
    }

    const titleMatch = line.match(/^title\s*:\s*(.+)$/i);
    if (titleMatch) {
      flushNode();
      title = titleMatch[1].trim();
      continue;
    }

    const descriptionMatch = line.match(/^description\s*:\s*(.+)$/i);
    if (descriptionMatch) {
      flushNode();
      description = descriptionMatch[1].trim();
      continue;
    }

    const tableMatch = line.match(/^\|\s*(\d+)\s*\|\s*([^|]+)\|\s*(.+?)\s*\|?$/);
    if (tableMatch) {
      flushNode();
      activeIndex = Number(tableMatch[1]);
      activeText = `${tableMatch[2].trim()} | ${tableMatch[3].trim()}`;
      continue;
    }

    const nodeMatch = line.match(/^(\d+)\s*[\).:-]?\s*(.+)$/);
    if (nodeMatch) {
      flushNode();
      activeIndex = Number(nodeMatch[1]);
      activeText = nodeMatch[2].trim();
      continue;
    }

    if (activeIndex != null) {
      activeText = `${activeText} ${line}`;
    }
  }

  flushNode();

  return {
    title,
    description,
    nodes: nodes.sort((a, b) => a.index - b.index),
  };
}

